import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, ConfirmModal, BulkSelectCell, ListPagination } from '@redpanda/ui';
import BankTransactionDialog from '../components/BankTransactionDialog';
import { TableSkeleton } from '@redpanda/ui';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { useInvoiceLimitCache } from '../hooks/useInvoiceLimitCache';
import { toast } from 'react-hot-toast';
import { Role } from '@redpanda/types';
import { useBankTransactionStore } from '../stores/bankAccountStore';
import { bulkDeleteBankTransactions } from '../services/api';
import { useBulkActions } from '@redpanda/utils';
import BulkActionButton from '../components/BulkOperations/BulkActionButton';
import BulkSelectHeader from '../components/BulkOperations/BulkSelectHeader';

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  isActive: boolean;
  description?: string;
  _count?: {
    transactions: number;
  };
}

interface BankTransaction {
  id?: string;
  transactionDate: string;
  description: string;
  transactionType: string;
  amount: number;
  reference?: string;
}

const BankTransactionPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const passedAccount = location.state?.account as BankAccount | null;
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const userRole = user?.role;

  const [account, setAccount] = useState<BankAccount | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<BankTransaction | null>(null);

  const canEdit = userRole && [Role.SUPER_ADMIN, Role.ADMIN, Role.USER].includes(userRole);

  // 使用發票限制快取 hook
  const {
    canAddInvoice,
    setCanAddInvoice,
    limitBannerRefreshTrigger,
    setLimitBannerRefreshTrigger,
  } = useInvoiceLimitCache();

  // 使用與銀行帳戶管理相同的 store 模式
  const {
    items: transactions,
    pagination,
    isLoading,
    showDeleteConfirmModal,
    isDeleting,
    fetchItems: fetchTransactions,
    setCurrentPage,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
    refreshData,
    addItem: addTransaction,
    updateItem: updateTransaction
  } = useBankTransactionStore(accountId || '');

  const displayTransactions = Array.isArray(transactions) ? transactions : [];

  // 批量操作功能
  const bulkActions = useBulkActions({
    items: displayTransactions,
    idField: 'id',
    entityName: t('bankTransactions.item'),
    bulkDeleteApi: (transactionIds: string[]) => bulkDeleteBankTransactions(accountId || '', transactionIds),
    canDeleteItem: () => true, // 交易記錄都可以刪除
    onOptimisticUpdate: (idsToRemove) => {
      // 注意：BankTransactionStore 是動態創建的，無法直接使用 createOptimisticBulkDeleteUpdate
      // 這裡使用 onSuccess 重新載入，不完全符合樂觀更新理念
      // 但由於架構限制，暫時保持這種方式
    },
    onSuccess: () => {
      // 重新載入數據以確保同步
      refreshData();
    },
    userRole: userRole,
    pagination: {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
    },
    fetchItems: fetchTransactions,
    setCurrentPage: setCurrentPage
  });

  // 最佳實務：保留對象訪問，只解構最常用的參數
  const { selectedItems, allSelected } = bulkActions;

  // 設定帳戶資訊（優先使用傳遞的資料）
  useEffect(() => {
    if (passedAccount && passedAccount.id === accountId) {
      setAccount(passedAccount);
    } else if (!passedAccount) {
      // 如果沒有傳遞資料（例如直接輸入 URL），跳回列表頁
      toast.error(t('bankTransactions.list.accessDenied'));
      navigate('/bank-accounts');
    }
  }, [passedAccount, accountId, t, navigate]);

  // 載入交易記錄
  useEffect(() => {
    if (isAuthenticated && !isUserLoading && accountId) {
      fetchTransactions(1);
    }
  }, [isAuthenticated, isUserLoading, accountId, fetchTransactions]);

  // 監聽銀行交易總數變化，用於單個刪除時更新 InvoiceLimitBanner
  const prevTransactionCountRef = useRef(transactions.length);
  useEffect(() => {
    const currentCount = transactions.length;
    const prevCount = prevTransactionCountRef.current;

    // 如果交易數量減少（表示有刪除操作），觸發 InvoiceLimitBanner 更新
    if (currentCount < prevCount && prevCount > 0) {
      setLimitBannerRefreshTrigger(prev => prev + 1);
    }

    prevTransactionCountRef.current = currentCount;
  }, [transactions.length, setLimitBannerRefreshTrigger]);

  // 格式化金額
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // 格式化交易類型
  const formatTransactionType = (type: string) => {
    return t(`bankTransactions.types.${type}`, { defaultValue: type });
  };

  // 處理關閉對話框
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingTransaction(null);
  };

  // 處理編輯
  const handleEdit = (transaction: BankTransaction) => {
    setEditingTransaction(transaction);
    setIsDialogOpen(true);
  };

  // 處理新增
  const handleAdd = () => {
    if (!canAddInvoice) {
      toast.error(t('bankTransactions.list.limitExceeded'));
      return;
    }
    setEditingTransaction(null);
    setIsDialogOpen(true);
  };


  // 處理分頁 - 使用批量操作的分頁處理函數
  const handlePageChange = (newPage: number) => {
    bulkActions.handlePageChange(newPage);
  };

  if (isUserLoading) {
    return <div className="app-loading bg-gray-50 dark:bg-slate-900 min-h-screen"></div>;
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題 */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div>
              {account ? (
                <>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {account.accountName}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {account.bankName} - {account.accountNumber} | {t('bankTransactions.currentBalance')}：<span className="font-semibold text-green-600">{formatAmount(account.currentBalance)}</span>
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('bankTransactions.list.title')}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('bankTransactions.list.loading')}</p>
                </>
              )}
            </div>
          </div>
          {canEdit && account && (
            <div className="flex space-x-3">
              <BulkActionButton
                selectedCount={selectedItems.length}
                loading={bulkActions.isBulkDeleting}
                onClick={bulkActions.handleBulkDelete}
                disabled={false} // 交易記錄都可以刪除，不需要禁用
                entityName={t('bankTransactions.item')}
              />
              <button
                onClick={handleAdd}
                disabled={!canAddInvoice}
                className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
                  canAddInvoice
                    ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={canAddInvoice ? undefined : t('bankTransactions.list.limitExceeded')}
              >
                {t('bankTransactions.list.addButton')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 發票限制橫幅 */}
      <InvoiceLimitBanner onLimitChange={setCanAddInvoice} refreshTrigger={limitBannerRefreshTrigger} />

      {/* 交易記錄表格 */}
      <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
        {isLoading ? (
          <TableSkeleton columns={canEdit ? 7 : 5} rows={5} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={displayTransactions.length > 0}
                  />
                  <TableHead className="w-[12%]">{t('bankTransactions.table.date')}</TableHead>
                  <TableHead className="w-[38%]">{t('bankTransactions.table.description')}</TableHead>
                  <TableHead className="w-[10%]">{t('bankTransactions.table.type')}</TableHead>
                  <TableHead className="w-[16%] text-right">{t('bankTransactions.table.amount')}</TableHead>
                  <TableHead className="w-[10%]">{t('bankTransactions.table.reference')}</TableHead>
                  {canEdit && <TableHead className="w-[14%]">{t('bankTransactions.table.actions')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-gray-500">
                      {t('bankTransactions.list.noTransactions')}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayTransactions.map((transaction, index) => (
                    <TableRow key={transaction.id || index}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={selectedItems.includes(transaction.id || '')}
                        onToggle={() => bulkActions.toggleItemSelection(transaction.id || '')}
                        canDelete={true}
                      />
                      <TableCell>
                        {new Date(transaction.transactionDate).toLocaleDateString('zh-TW')}
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.amount > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {formatTransactionType(transaction.transactionType)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatAmount(transaction.amount)}
                        </span>
                      </TableCell>
                      <TableCell>{transaction.reference || '-'}</TableCell>
                      {canEdit && (
                        <TableCell>
                          <button
                            onClick={() => handleEdit(transaction)}
                            className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 mr-3 text-sm"
                          >
                            {t('common.actions.edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(transaction.id || '')}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-sm"
                          >
                            {t('common.actions.delete')}
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>


      {/* 分頁 */}
      <ListPagination pagination={pagination} onPageChange={handlePageChange} className="mt-6" />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('bankTransactions.list.confirmDeleteTitle')}
        message={t('bankTransactions.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* 批量刪除確認對話框 */}
      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('bankTransactions.list.confirmBulkDeleteTitle')}
        message={t('bankTransactions.list.confirmBulkDeleteMessage', { count: selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
      />

      {/* 新增/編輯對話框 */}
      <BankTransactionDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        accountId={accountId || ''}
        transaction={editingTransaction}
        onTransactionCreated={(newTransaction) => {
          addTransaction(newTransaction);
          // 新增銀行交易後觸發發票限制刷新
          setLimitBannerRefreshTrigger(prev => prev + 1);
        }}
        onTransactionUpdated={(updatedTransaction) => {
          if (updatedTransaction && editingTransaction) {
            updateTransaction(editingTransaction.id!, updatedTransaction);
          }
        }}
      />
    </div>
  );
};

export default BankTransactionPage;